import { Alert, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { EmptyState } from '@/shared/ui/EmptyState'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { isCurrentPeriod, type EmploymentPeriod } from './hrApi'

interface EmploymentHistoryProps {
  periods: readonly EmploymentPeriod[]
  loading: boolean
}

/**
 * Tayinlashlar tarixi — O'ZGARMAS jurnal.
 *
 * <h2>⚠️ BU YERDA TAHRIRLASH VA O'CHIRISH TUGMASI YO'Q</h2>
 * Va u kelajakda ham qo'shilmaydi. Mijoz talabi so'zma-so'z:
 * <i>"muhim xodim ma'lumoti ustiga yozilmasin — sana, foydalanuvchi va
 * sabab bilan tarix saqlansin"</i> (`QARORLAR.md` §20.9-E).
 *
 * Uch qatlam bir xil gapiradi:
 *   · bazada — `hr.employment_immutable()` triggeri `UPDATE` ni rad etadi;
 *   · API'da — `EmploymentController` da faqat `GET` va `POST` bor;
 *   · bu yerda — amal ustuni umuman chizilmaydi.
 * Panelda tugma bo'lsa-yu, backend uni rad etsa, operator buni NUQSON
 * deb hisoblardi — holbuki bu qoidaning o'zi.
 *
 * O'zgartirish yagona yo'l bilan bo'ladi: YANGI tayinlash, u eskisini
 * yopadi.
 *
 * <h2>⚠️ TUGUN NOMI — BUGUNGI NOM, O'SHA PAYTDAGISI EMAS</h2>
 * U `platform.org_node` dan JOIN bilan olinadi
 * (`EmploymentPeriodView.orgNodeName` javadoc'i): nomni tarixga
 * NUSXALASH §20.2 ni buzardi va "Mazar filiali" qayta nomlanganda ikki
 * xil nom paydo bo'lardi. Ya'ni "2024-yil martda BU tugunda ishlagan"
 * savoliga javob `orgNodeId` beradi, nom faqat ko'rsatish uchun — buni
 * ekranda ham aytamiz.
 *
 * <h2>⚠️ SANALAR — KUN, LAHZA EMAS</h2>
 * `validFrom`/`validTo` `LocalDate` va ular vaqt zonasidan MUSTAQIL.
 * Ularga `formatDateTime` qo'llash `Asia/Kabul` (UTC+4:30) da kunni
 * siljitib yuborardi — shuning uchun ular xom holda ko'rsatiladi.
 * `createdAt` esa `Instant` va u tenant zonasida ko'rsatiladi.
 */
export function EmploymentHistory({ periods, loading }: EmploymentHistoryProps) {
  const { t } = useTranslation()

  // ⚠️ N4: `employment_history.changed_by` — `auth.getName()`, aktyor
  //    identifikatori. Nom `actorName.ts` xaritasidan keladi.
  const actors = useActorDirectory()

  const columns: ColumnsType<EmploymentPeriod> = [
    {
      key: 'period',
      title: t('hr.history.period'),
      width: 210,
      render: (_, row) => (
        <Space size={6} wrap>
          <Typography.Text strong={isCurrentPeriod(row)}>
            {row.validFrom} → {row.validTo ?? '…'}
          </Typography.Text>
          {isCurrentPeriod(row) ? <Tag color="green">{t('hr.history.current')}</Tag> : null}
        </Space>
      ),
    },
    {
      key: 'jobTitle',
      title: t('hr.assignment.jobTitle'),
      width: 180,
      render: (_, row) => row.jobTitleName ? seededName('jobTitles', row.jobTitleCode, row.jobTitleName, t) : (row.jobTitleCode ?? '—'),
    },
    {
      key: 'orgNode',
      title: t('hr.assignment.orgNode'),
      width: 200,
      render: (_, row) => (
        <Tooltip title={row.orgNodeCode ?? ''}>
          <span>{row.orgNodeName ?? row.orgNodeCode ?? '—'}</span>
        </Tooltip>
      ),
    },
    {
      key: 'employmentType',
      title: t('hr.assignment.employmentType'),
      width: 140,
      // ⚠️ `defaultValue` YO'Q va bo'sh qiymat tekshiruvi ham yo'q:
      // ustun `NOT NULL` va qiymatlar to'plami bazada
      // (`employment_type_valid`), serverda (`EmploymentType`) va shu
      // yerda (`EMPLOYMENT_TYPES`) yopilgan. "Noma'lum qiymat" yo'li
      // o'lik shox bo'lardi — u sinovdan o'tmagani uchun bir kun
      // jimgina noto'g'ri ishlab ketardi.
      render: (_, row) => t(`employmentType.${row.employmentType}`),
    },
    {
      key: 'reason',
      title: t('hr.history.reason'),
      render: (_, row) => row.reason ?? '—',
    },
    {
      key: 'changedBy',
      title: t('hr.history.changedBy'),
      width: 170,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>
            {actorLabel(row.changedBy, actors, t('common.dash'))}
          </Typography.Text>
          {row.createdAt === null ? null : (
            <Tooltip title={utcTooltip(row.createdAt)}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {formatDateTime(row.createdAt)}
              </Typography.Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('hr.history.immutableNote')}
      />
      <Table<EmploymentPeriod>
        rowKey={(row) => row.id}
        columns={columns}
        dataSource={[...periods]}
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 1000 }}
        locale={{
          emptyText: (
            <EmptyState
              variant="no-data"
              title={t('hr.history.empty')}
              description={t('hr.history.emptyHint')}
            />
          ),
        }}
      />
    </>
  )
}
